import { useCallback, useEffect, useState } from "react";
import { X, Plus, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * Two-pane outlet manager for a beat.
 * Left  = outlets currently on this beat.
 * Right = search + filter unassigned/other outlets, select, "Move to this beat".
 */
export default function BeatOutletsManager({ beat, onOpenChange }) {
  const beatId = beat?.id;
  const open = !!beatId;

  const [leftItems, setLeftItems] = useState([]);
  const [leftTotal, setLeftTotal] = useState(0);
  const [rightItems, setRightItems] = useState([]);
  const [rightTotal, setRightTotal] = useState(0);
  const [rightQ, setRightQ] = useState("");
  const [rightPage, setRightPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const loadLeft = useCallback(async () => {
    if (!beatId) return;
    const { data } = await api.get(`/beats/${beatId}/outlets`, { params: { page_size: 200 } });
    setLeftItems(data.items); setLeftTotal(data.total);
  }, [beatId]);

  const loadRight = useCallback(async () => {
    if (!beatId) return;
    const params = { page: rightPage, page_size: 50, status: "active" };
    if (rightQ) params.q = rightQ;
    const { data } = await api.get("/retailers", { params });
    // Filter out those already on this beat (they might not appear if server-side beat_id filter is used;
    // we opt for a broader list and dedup client-side to keep the toolbar simple).
    const already = new Set(leftItems.map(o => o.id));
    setRightItems((data.items || []).filter(o => !already.has(o.id)));
    setRightTotal(data.total);
  }, [beatId, rightPage, rightQ, leftItems]);

  useEffect(() => { if (open) loadLeft(); }, [open, loadLeft]);
  useEffect(() => { if (open) loadRight(); }, [open, loadRight]);
  useEffect(() => { if (!open) { setSelected(new Set()); setRightQ(""); setRightPage(1); } }, [open]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const doAssign = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/beats/${beatId}/assign-outlets`,
                                      { retailer_ids: [...selected] });
      toast.success(`Moved ${data.modified} outlet(s) to ${beat.code}`);
      setSelected(new Set());
      await Promise.all([loadLeft(), loadRight()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doUnassign = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/beats/${beatId}/unassign-outlets`,
                                      { retailer_ids: [id] });
      toast.success(`Removed ${data.modified} outlet from beat`);
      await Promise.all([loadLeft(), loadRight()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
                     data-testid="beat-outlets-dialog">
        <DialogHeader>
          <DialogTitle>Manage outlets — {beat?.code} · {beat?.name}</DialogTitle>
          <DialogDescription>
            Left: outlets currently on this beat ({leftTotal}). Right: search all active outlets and
            move selected into this beat.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 flex-1 overflow-hidden">
          {/* Left */}
          <Card className="border-neutral-200 flex flex-col overflow-hidden">
            <CardContent className="p-3 flex-1 flex flex-col">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                On beat ({leftTotal})
              </div>
              <div className="flex-1 overflow-y-auto space-y-1"
                   data-testid="beat-outlets-left">
                {leftItems.map(o => (
                  <div key={o.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-neutral-50">
                    <div>
                      <div className="font-mono">{o.code}</div>
                      <div className="text-neutral-500">{o.name}</div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => doUnassign(o.id)} disabled={busy}
                            data-testid={`beat-outlets-remove-${o.id}`}>
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {leftItems.length === 0 && (
                  <div className="text-center text-neutral-400 text-xs py-8">
                    No outlets on this beat yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right */}
          <Card className="border-neutral-200 flex flex-col overflow-hidden">
            <CardContent className="p-3 flex-1 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Input value={rightQ} onChange={(e) => { setRightQ(e.target.value); setRightPage(1); }}
                       placeholder="Search outlets…" className="h-9 text-xs"
                       data-testid="beat-outlets-right-search" />
                <Button size="sm" onClick={doAssign} disabled={busy || selected.size === 0}
                        className="tulsi-gradient text-white font-semibold h-9 whitespace-nowrap"
                        data-testid="beat-outlets-assign">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> :
                    <><ArrowRight className="h-3 w-3 mr-1" /> Move {selected.size ? `(${selected.size})` : ""}</>}
                </Button>
              </div>
              <div className="text-[11px] text-neutral-500">
                {rightTotal.toLocaleString()} active outlets · showing {rightItems.length}
              </div>
              <div className="flex-1 overflow-y-auto space-y-0.5" data-testid="beat-outlets-right">
                {rightItems.map(o => (
                  <label key={o.id}
                         className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer hover:bg-neutral-50 ${
                           selected.has(o.id) ? "bg-[#E5197F]/5" : ""
                         }`}
                         data-testid={`beat-outlets-right-row-${o.id}`}>
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
                    <div className="flex-1">
                      <div className="font-mono">{o.code}</div>
                      <div className="text-neutral-500">{o.name}</div>
                    </div>
                    {o.beat_id && o.beat_id !== beatId && (
                      <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-700">on another beat</span>
                    )}
                  </label>
                ))}
              </div>
              <div className="flex justify-between text-[11px] text-neutral-500">
                <span>Page {rightPage}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setRightPage(p => Math.max(1, p - 1))}
                          disabled={rightPage === 1}>Prev</Button>
                  <Button size="sm" variant="ghost" onClick={() => setRightPage(p => p + 1)}
                          disabled={rightItems.length < 50}>Next</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
